import { END_TIME, START_TIME } from "@/components/TemporalValue/TempTimeline/redux";
import { isTemporal } from "@/pages/decisions/DecisionDashboard/Reports/Details/UnifiedTimeline/types";
import type { Node } from "@/pages/models/release/Test/GraphTest/redux";
import Button from "@imminently/decisively-ui/Button";
import DateTimeInput from "@imminently/decisively-ui/DateTimeInput";
import Icon from "@imminently/decisively-ui/Icon";
import clsx from "clsx";
import React from "react";
import type { Props } from "./DatesFilter_types";

export const _: React.FC<Props> = React.memo((p) => {
  const { className, hide, set: setDatesFilter, value: datesFilter, minMax } = p;
  const [start, end] = datesFilter || [null, null];

  const onChange = React.useCallback(
    (v: Date | null) => setDatesFilter([v?.toISOString() ?? null, v?.toISOString() ?? null]),
    [setDatesFilter],
  );

  const hasValue = start !== null && end !== null;

  const onIconButtonClick = React.useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    (e) => {
      if (!hasValue) return;

      e.stopPropagation();
      setDatesFilter([null, null]);
    },
    [hasValue, setDatesFilter],
  );

  const startDate = React.useMemo(() => (start ? new Date(start) : null), [start]);

  return (
    <div className={clsx("flex h-10 items-center gap-2", className, hide && "hidden")}>
      <DateTimeInput
        className="w-36"
        dateOnly
        serialize={false}
        value={startDate}
        onValueChange={onChange}
        minDate={minMax?.[0]}
        maxDate={minMax?.[1]}
        placeholder="Pick a date"
        aria-label="Date filter"
        fullWidth
      />

      {hasValue ? (
        <Button
          icon
          onClick={onIconButtonClick}
          label="Clear date"
        >
          <Icon name="close" />
        </Button>
      ) : null}
    </div>
  );
});
_.displayName = "components/RuleGraph/DatesFilter";

export * from "./DatesFilter_types";
export * from "./DatesFilter_mergeValueAndDatesFilter";

// export const mergeValueAndDatesFilter = (v: Node['value'], datesFilter: Props[ 'value' ]): typeof v => {
//   if(!datesFilter || !isTemporal(v)) return v;

//   const [dFilt0, dFilt1] = datesFilter;
//   if(dFilt0 === null || dFilt1 === null) return v;

//   const start = dFilt0.split('T')[ 0 ];
//   const end = dFilt1.split('T')[ 0 ];

//   const nextDerived = v.temporal.ranges.reduce< Node['value'] >(
//     (a, { r, v }) => {
//       if(a !== undefined) return a;

//       const [rangeStart] = r.start.split('T');
//       const [rangeEnd] = r.end.split('T');
//       if(r.start !== START_TIME && rangeStart > start) return a;
//       if(r.end !== END_TIME && rangeEnd < end) return a;

//       return typeof v === 'object' && v !== null ? undefined : v;
//     },
//     undefined,
//   );

//   return nextDerived;
// };

export const deriveMinMaxFromNodeValues = (vs: Node["value"][]): Props["minMax"] => {
  const dfltAcc: { min: string | null; max: string | null } = { max: null, min: null };

  const { min, max } = vs.reduce((acc, derived) => {
    if (!isTemporal(derived)) return acc;

    return derived.temporal.ranges.reduce((a, { r: { end, start } }) => {
      const rtrn: typeof acc = {
        max: end === END_TIME ? a.max : a.max === null ? end : a.max < end ? end : a.max,
        min: start === START_TIME ? a.min : a.min === null ? start : a.min > start ? start : a.min,
      };

      return rtrn;
    }, acc);
  }, dfltAcc);

  return min === null || max === null ? undefined : [new Date(min), new Date(max)];
};
